import { api } from './client'
import { requireArrayIn } from './readShape'
import { GENERATE_TIMEOUT_MS } from '@utils/billGeneration'

const UNBILLED_SHAPE = { noun: 'list of unbilled entries', emptyNoun: 'list' }

/**
 * Spec 149. cb-api's answer says what the Add Entries list is: `open` carries the
 * entries, and `posted` / `no-matter` carry none on purpose (`lineItems: null`)
 * and say why. Any other answer is a read we cannot interpret, so it is refused
 * the same way as an `open` answer with no list: never shown as an empty one.
 */
const readUnbilled = (body) => {
  const status = body?.data?.status
  if (status === 'posted' || status === 'no-matter') return body
  requireArrayIn(status === 'open' ? body : undefined, 'lineItems', UNBILLED_SHAPE)
  return body
}

/**
 * Spec 158. The Generate Bills reads, refused rather than shown when they are not the
 * documented shape: an unreadable setting list or matter list must never render as
 * "your firm has none".
 */
const unreadable = (noun) => new Error(`The ${noun} came back in a shape we couldn't read. This is not an empty list — try again, and report it if it persists.`)

const readGenerateOptions = (body) => {
  const d = body?.data
  const settings = d?.settings
  if (!Array.isArray(settings) || typeof d.timeEntryApproval !== 'boolean'
    || !settings.every((s) => s && Number.isInteger(s.id) && typeof s.name === 'string' && typeof s.isDefault === 'boolean')) {
    throw unreadable('list of billing settings')
  }
  return body
}

// `splitBilling` and each row's `split` (spec 158 §3.6, review fix) are required: a list
// from a cb-api that did not look for split-billed matters must not be offered as if it had.
const readGenerateCandidates = (body) => {
  const d = body?.data
  requireArrayIn(body, 'rows', { noun: 'list of matters to bill', emptyNoun: 'list' })
  if (!Number.isInteger(d.rowCount) || typeof d.complete !== 'boolean' || typeof d.splitBilling !== 'boolean'
    || !d.rows.every((r) => r && typeof r.matterId === 'string' && typeof r.matterName === 'string' && typeof r.split === 'boolean')) {
    throw unreadable('list of matters to bill')
  }
  return body
}

const invoiceService = {
  getInvoice: (id) => api.get(`/invoices/${id}`),
  saveInvoice: (id, data) => api.post(`/invoices/${id}`, data),
  deleteInvoice: (id) => api.delete(`/invoices/${id}`),

  getLineItems: (id, params = {}) => api.get(`/invoices/${id}/line-items`, params),
  // `params`: `{ startDate, endDate }` as YYYY-MM-DD for the invoice's own period,
  // or nothing for every date.
  getUnbilledEntries: async (id, params = {}) => readUnbilled(await api.get(`/invoices/${id}/unbilled`, params)),
  manageEntries: (id, data) => api.post(`/invoices/${id}/manage-entries`, data),

  getInterest: (id) => api.get(`/invoices/${id}/interest`),
  checkStatus: (id) => api.post(`/invoices/${id}/check-status`),
  getFeeArrangement: (id) => api.get(`/invoices/${id}/fee-arrangement`),
  convertToFlatRate: (id, data) => api.post(`/invoices/${id}/flat-rate`, data),

  getRate: (id, params) => api.get(`/invoices/${id}/rate`, params),
  saveTimeExpense: (id, entry) => api.post(`/invoices/${id}/time-expense`, entry),
  getTemplate: (id) => api.get(`/invoices/${id}/template`),

  // Generate Bills (spec 158): CB's Billing Home path, one matter per generateBill call.
  getGenerateOptions: async () => readGenerateOptions(await api.get('/invoices/generate/options')),
  // `period`: `{ settingsId, startDate?, endDate }` as YYYY-MM-DD.
  getGenerateCandidates: async (period) => readGenerateCandidates(await api.post('/invoices/generate/candidates', period)),
  // ONE matter: `{ matterId, settingsId, startDate?, endDate, issueDate }`. A write:
  // never retried, and given longer than the client's 30s default because a timeout
  // here leaves the outcome unknown (utils/billGeneration.js).
  generateBill: (request) => api.post('/invoices/generate', request, { timeout: GENERATE_TIMEOUT_MS }),
  canManageBilling: () => api.get('/invoices/can-manage'),

  // Bill generation / recurring billing
  getBillingSettings: () => api.get('/invoices/billing-settings'),
  filterBillableObjects: (settings) => api.post('/invoices/billing-filter', settings),
  getBillingSummary: (data) => api.post('/invoices/billing-summary', data),
  saveBillingSettings: (settings) => api.post('/invoices/billing-settings/save', settings),
}

export default invoiceService
